/**
 * Session selector — the per-car recordings list (public-viewer MVP,
 * the "session surfacer" companion to StageStrip).
 *
 * One row per recorded session of the selected car, newest first:
 * stage tag (SS2/H1 from M3 stage_index; · when unattributed), start
 * time, duration, and a LIVE row while the car streams. Tapping a row
 * mounts that session as VOD (sessionPick in BrowseStateView — the
 * keyed FeedPlayer remount, M21); tapping the LIVE row returns to the
 * live feed. The mounted row highlights.
 *
 * Times render in the BROWSER's local timezone (same MVP approximation
 * as the stage status chips — correct when the fan and the event share
 * a timezone).
 */

import React from 'react';
import { Play, Radio } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface DriverSessionRow {
  sessionId: string;
  startUtcMs: number | null;
  endUtcMs: number | null;
  /** Segment count — duration fallback for sessions that never wrote an
   *  end timestamp (crash/kill mid-recording; footage still exists). */
  segmentCount: number | null;
  /** SS2/H1 label; null = unattributed session. */
  stageTag: string | null;
  /** This is the car's CURRENT live session. */
  isLive: boolean;
}

function fmtClock(utcMs: number | null): string {
  if (utcMs == null) return '--:--';
  const d = new Date(utcMs);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Compact day-month ('2 Jul') between time and duration — a multi-day
 *  event's rows are ambiguous on HH:MM alone. Today renders as
 *  'today' so the common single-day case stays glanceable. */
function fmtDate(utcMs: number | null): string {
  if (utcMs == null) return '';
  const d = new Date(utcMs);
  const now = new Date();
  if (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  ) {
    return 'today';
  }
  return `${d.getDate()} ${d.toLocaleString(undefined, { month: 'short' })}`;
}

/** HLS segment length (s) — matches the phone encoder's cut cadence.
 *  Only used to ESTIMATE a duration for end-less sessions. */
const SEGMENT_SECONDS = 6;

const fmtMinSec = (s: number): string => {
  const total = Math.round(s);
  // Hours tier — an all-day rally recording rendered "979:28" in m:ss.
  if (total >= 3600) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}:${String(m).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

function fmtDuration(row: DriverSessionRow): string {
  if (row.isLive) return 'live';
  if (row.startUtcMs != null && row.endUtcMs != null && row.endUtcMs > row.startUtcMs) {
    return fmtMinSec((row.endUtcMs - row.startUtcMs) / 1000);
  }
  // No end timestamp (recording died without a clean stop) but the
  // footage is real — estimate from the segment count so the row
  // doesn't read as broken/duplicated next to its healthy siblings.
  if (row.segmentCount != null && row.segmentCount > 0) {
    return `~${fmtMinSec(row.segmentCount * SEGMENT_SECONDS)}`;
  }
  return '—';
}

export const SessionSelector: React.FC<{
  rows: DriverSessionRow[];
  /** Session currently on the stage — highlights its row. */
  mountedSessionId: string | null;
  onPick: (sessionId: string) => void;
  className?: string;
}> = ({ rows, mountedSessionId, onPick, className }) => {
  if (rows.length === 0) return null;
  return (
    <div className={cn('border-b border-border-subtle p-3.5', className)}>
      <div className="mb-2.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-fg-muted">
        <span>Sessions</span>
        <span className="font-mono font-medium normal-case tracking-normal">{rows.length}</span>
      </div>
      <div className="flex flex-col gap-1">
        {rows.map((row) => {
          const active = row.sessionId === mountedSessionId;
          return (
            <button
              key={row.sessionId}
              type="button"
              onClick={() => onPick(row.sessionId)}
              className={cn(
                'flex w-full items-center gap-2 rounded-sm border px-2 py-1.5 text-left',
                active
                  ? 'border-accent/40 bg-bg-elevated shadow-[inset_2px_0_0_var(--accent-primary)]'
                  : 'border-transparent bg-bg-surface hover:bg-bg-elevated',
              )}
            >
              <span
                className={cn(
                  'w-9 shrink-0 rounded-[3px] px-1 py-px text-center font-mono text-[9px] font-bold tracking-wide',
                  row.isLive
                    ? 'bg-status-error text-white'
                    : row.stageTag != null
                      ? 'bg-black/40 text-accent-cyan'
                      : 'bg-bg-base text-fg-muted',
                )}
              >
                {row.isLive ? 'LIVE' : row.stageTag ?? '·'}
              </span>
              <span className="font-mono text-[12px] font-semibold text-fg">
                {fmtClock(row.startUtcMs)}
              </span>
              <span className="truncate font-mono text-[10px] text-fg-muted">
                {fmtDate(row.startUtcMs)}
              </span>
              <span className="ml-auto font-mono text-[11px] text-fg-secondary">
                {fmtDuration(row)}
              </span>
              {row.isLive ? (
                <Radio className="size-3 shrink-0 text-status-error" />
              ) : (
                <Play className={cn('size-3 shrink-0', active ? 'text-accent' : 'text-fg-muted')} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SessionSelector;
